import { ref } from 'vue'
import type { Chat as Conversation } from 'models'
import { getTextPart, stripMarkdown } from 'utils'
import { useDialogConfirm } from '../useDialogConfirm'

/**
 * Single-line preview for a conversation: explicit title, otherwise the
 * first user message. Used as list label and as search haystack.
 */
export function getConversationPreview(conversation: Conversation): string {
    return (
        stripMarkdown(conversation.title) ||
        stripMarkdown(
            getTextPart(
                // The list endpoint isn't Zod-parsed: don't trust the
                // declared types at runtime
                conversation.messages?.find(
                    (message) => message.role === 'user',
                ),
            ),
        ) ||
        ''
    )
}

/**
 * Inline-rename and delete-with-confirm state shared by the conversation
 * lists (PkChatbotViewConversations, PkChatSidebar). Renames are optimistic:
 * on failure the next conversations load reconciles with the server state.
 */
export function useConversationActions(actions: {
    renameChatTitle: (chatId: string, title: string) => Promise<unknown>
    deleteChat: (chatId: string) => Promise<unknown>
}) {
    const editingId = ref<string | null>(null)
    const editingTitle = ref('')

    const startEdit = (conversation: Conversation, fallbackTitle = '') => {
        editingId.value = conversation.id
        editingTitle.value = stripMarkdown(conversation.title) || fallbackTitle
    }

    const confirmEdit = async (id: string) => {
        const trimmed = editingTitle.value.trim()
        editingId.value = null
        if (trimmed && trimmed.length <= 255) {
            try {
                await actions.renameChatTitle(id, trimmed)
            } catch {
                // Revert optimistically: reload will pick up server state
            }
        }
    }

    const cancelEdit = () => {
        editingId.value = null
    }

    const onRenameKeydown = (event: KeyboardEvent, id: string) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            confirmEdit(id)
        } else if (event.key === 'Escape') {
            event.preventDefault()
            cancelEdit()
        }
    }

    const { openDialogConfirm } = useDialogConfirm()
    const requestDelete = async (id: string) => {
        try {
            const proceed = await openDialogConfirm()
            if (!proceed) {
                return
            }
            await actions.deleteChat(id)
        } catch {
            // Silently fail: next load will reconcile
        }
    }

    return {
        editingId,
        editingTitle,
        startEdit,
        confirmEdit,
        cancelEdit,
        onRenameKeydown,
        requestDelete,
    }
}
